import React, { Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Layout from "@/components/layout/Layout";
import { useScrollToTop } from "@/hooks/useScrollToTop";

// Lazy-loaded page components
// const Home = React.lazy(() => import("@/pages/Home"));
const About = React.lazy(() => import("@/pages/About"));
const Doctors = React.lazy(() => import("@/pages/Doctors"));
const DoctorProfile = React.lazy(() => import("@/pages/DoctorProfile"));
const Departments = React.lazy(() => import("@/pages/Departments"));
const DepartmentDetail = React.lazy(() => import("@/pages/DepartmentDetail"));
const Appointment = React.lazy(() => import("@/pages/Appointment"));
const Contact = React.lazy(() => import("@/pages/Contact"));
const PrivacyPolicy = React.lazy(() => import("@/pages/PrivacyPolicy"));
const WasteManagement = React.lazy(() => import("@/pages/WasteManagement"));
const Facilities = React.lazy(() => import("@/pages/Facilities"));
const RareCases = React.lazy(() => import("@/pages/RareCases"));

const pageTransition = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.3, ease: "easeInOut" },
};

function AnimatedPage({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={pageTransition.initial}
      animate={pageTransition.animate}
      exit={pageTransition.exit}
      transition={pageTransition.transition}
    >
      {children}
    </motion.div>
  );
}

function LoadingFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
      <h1 className="text-6xl font-bold text-primary">404</h1>
      <h2 className="text-2xl font-semibold text-gray-800">Page Not Found</h2>
      <p className="max-w-md text-gray-600">
        The page you are looking for does not exist or has been moved.
      </p>
      <a
        href="/"
        className="mt-4 inline-flex items-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary/90"
      >
        Go Back Home
      </a>
    </div>
  );
}

function ScrollToTopWrapper() {
  useScrollToTop();
  return null;
}

export default function App() {
  const location = useLocation();

  return (
    <>
      <ScrollToTopWrapper />
      <Layout>
        <Suspense fallback={<LoadingFallback />}>
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route
                path="/"
                element={
                  <AnimatedPage>
                    <Appointment />
                  </AnimatedPage>
                }
              />
              <Route
                path="/about"
                element={
                  <AnimatedPage>
                    <About />
                  </AnimatedPage>
                }
              />
              <Route
                path="/doctors"
                element={
                  <AnimatedPage>
                    <Doctors />
                  </AnimatedPage>
                }
              />
              <Route
                path="/doctors/:id"
                element={
                  <AnimatedPage>
                    <DoctorProfile />
                  </AnimatedPage>
                }
              />
              <Route
                path="/departments"
                element={
                  <AnimatedPage>
                    <Departments />
                  </AnimatedPage>
                }
              />
              <Route
                path="/departments/:id"
                element={
                  <AnimatedPage>
                    <DepartmentDetail />
                  </AnimatedPage>
                }
              />
              <Route
                path="/appointment"
                element={
                  <AnimatedPage>
                    <Appointment />
                  </AnimatedPage>
                }
              />
              <Route
                path="/contact"
                element={
                  <AnimatedPage>
                    <Contact />
                  </AnimatedPage>
                }
              />
              <Route
                path="/privacy-policy"
                element={
                  <AnimatedPage>
                    <PrivacyPolicy />
                  </AnimatedPage>
                }
              />
              <Route
                path="/facilities"
                element={
                  <AnimatedPage>
                    <Facilities />
                  </AnimatedPage>
                }
              />
              <Route
                path="/rare-cases"
                element={
                  <AnimatedPage>
                    <RareCases />
                  </AnimatedPage>
                }
              />
              <Route
                path="/waste-management"
                element={
                  <AnimatedPage>
                    <WasteManagement />
                  </AnimatedPage>
                }
              />
              <Route
                path="*"
                element={
                  <AnimatedPage>
                    <NotFound />
                  </AnimatedPage>
                }
              />
            </Routes>
          </AnimatePresence>
        </Suspense>
      </Layout>
    </>
  );
}
